import {
  HoveredShapeStyle,
  SelectedShapeStyle,
} from '../SheetsViewerV2.config';
import {
  AbstractSheetFilledShape,
  SheetFillStyleSet,
} from './abstract/AbstractSheetFilledShape';
import { LineStyle } from './abstract/AbstractSheetLineShape';
import { HatchPatternConfig, HatchPatternType } from './utils/hatch-patterns';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { SheetShapeDeepFragment } from 'src/gql/graphql';
import { PdfSpaceTextRenderer } from 'src/components/viewers/SheetsViewerV2/utils/text-rendering';
import { decodeColorWithPattern } from 'src/utils/color-pattern-encoding';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import { ChakraColor } from 'src/utils/color-util';

// Entry type for legend rendering with decoded color/pattern info
type LegendEntry = {
  id: string;
  name: string;
  /** Original encoded color string (may contain pattern or gradient) */
  encodedColor: string | null | undefined;
  /** Decoded base color (hex or gradient) */
  baseColor: string;
  /** Decoded hatch pattern, if any */
  hatchPattern: HatchPatternConfig | null;
  /** Whether the base color is a gradient */
  isGradient: boolean;
  /** For gradients, the extracted colors */
  gradientColors: string[];
};

export class LegendShape extends AbstractSheetFilledShape {
  // Layout constants (in viewer coordinates)
  static readonly ROW_HEIGHT = 0.35;
  static readonly PADDING = 0.12;
  static readonly COLOR_SWATCH_SIZE = 0.2;
  static readonly TEXT_HEIGHT = 0.18; // Increased from 0.08 for better readability
  static readonly TEXT_PADDING = 0.08;
  static readonly MIN_WIDTH = 2.0;

  private orderName = '';
  private entries: LegendEntry[] = [];
  private legendPosition: THREE.Vector3 | null = null;
  private textRenderer: PdfSpaceTextRenderer | null = null;
  private shapeId: string;
  private textRenderPromise: Promise<void> | null = null;
  private swatchShapes: Autodesk.Edit2D.Polygon[] = []; // Store swatches separately
  private hatchLineShapes: Autodesk.Edit2D.Shape[] = []; // Store hatch lines separately
  private selectedEntryIds: Set<string> = new Set();

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    point?: THREE.Vector3,
    styleSet?: SheetFillStyleSet,
    /** Pre-computed entries for aggregated orders */
    precomputedEntries?: Array<{
      id: string;
      name: string;
      color: string | null | undefined;
    }>,
    /** Selected entry IDs for visual highlighting */
    selectedEntryIds?: Set<string>
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);

    this.shapeId = shapeModel?.id || `legend-${Date.now()}`;

    // Extract order entries from the shape model's legendOrder
    if (shapeModel?.sheetShapeLegend?.legendOrder) {
      const legendOrder = shapeModel.sheetShapeLegend.legendOrder;
      this.orderName = legendOrder.name;

      // Use precomputed entries for aggregated orders, otherwise use orderEntries
      const sourceEntries =
        precomputedEntries ||
        (legendOrder.orderEntries || []).map((e) => ({
          id: e.id,
          name: e.name,
          color: e.color,
        }));

      this.entries = sourceEntries.map((entry) => {
        const encodedColor = entry.color;
        const { color: baseColor, pattern: hatchPattern } =
          decodeColorWithPattern(encodedColor || '');
        const isGradient = baseColor.startsWith('linear-gradient');
        const gradientColors = isGradient
          ? this.extractGradientColors(baseColor)
          : [];

        return {
          id: entry.id,
          name: entry.name,
          encodedColor,
          baseColor:
            isGradient && gradientColors.length > 0
              ? gradientColors[0]
              : baseColor,
          hatchPattern,
          isGradient,
          gradientColors,
        };
      });
    }

    // Store selected entry IDs for visual highlighting
    this.selectedEntryIds = selectedEntryIds || new Set();

    const position = this.getPosition(shapeModel, point);
    this.legendPosition = position;
    this.createLegendShapes(position);

    this.textLabelPoints = [];
    this.styleSet = styleSet || this.getDefaultStyleSet();
    this.setStyle(SheetShapeStyleType.default);
  }

  private getPosition(
    shapeModel?: SheetShapeDeepFragment,
    point?: THREE.Vector3
  ): THREE.Vector3 {
    if (shapeModel?.sheetShapeLegend) {
      const converted = this.convertPointToRender(
        shapeModel.sheetShapeLegend.position
      );
      return new THREE.Vector3(converted.x, converted.y, 0);
    } else if (point) {
      return point;
    }
    throw new Error('Either shapeModel with legend or point must be provided');
  }

  private getDefaultStyleSet(): SheetFillStyleSet {
    return {
      default: {
        lineWidth: 1,
        fillAlpha: 1,
        lineColor: ChakraColor.gray200,
        fillColor: '#FFFFFF',
        lineStyle: LineStyle.Solid,
      },
      hovered: {
        ...HoveredShapeStyle,
        lineWidth: 2,
        fillAlpha: 1,
        lineColor: ChakraColor.blue500,
        fillColor: '#FFFFFF',
      },
      selected: {
        ...SelectedShapeStyle,
        lineWidth: 2,
        fillAlpha: 1,
      },
    };
  }

  /**
   * Extract colors from a CSS linear-gradient string
   */
  private extractGradientColors(gradientString: string): string[] {
    const hexColorRegex = /#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\b/g;
    const matches = gradientString.match(hexColorRegex);
    return matches || [];
  }

  private calculateTableWidth(): number {
    // Calculate width based on longest entry name
    // Approximate: each character is roughly 0.5x the text height
    const charWidth = LegendShape.TEXT_HEIGHT * 0.5;
    const maxNameLength = Math.max(
      ...this.entries.map((e) => e.name.length),
      10 // minimum 10 chars
    );
    const textWidth = maxNameLength * charWidth;

    return Math.max(
      LegendShape.MIN_WIDTH,
      LegendShape.PADDING +
        LegendShape.COLOR_SWATCH_SIZE +
        LegendShape.TEXT_PADDING +
        textWidth +
        LegendShape.PADDING
    );
  }

  private createLegendShapes(position: THREE.Vector3): void {
    const shapes: Autodesk.Edit2D.Shape[] = [];

    const numEntries = this.entries.length;

    // Calculate dimensions
    const tableWidth = this.calculateTableWidth();
    const tableHeight =
      LegendShape.PADDING +
      Math.max(1, numEntries) * LegendShape.ROW_HEIGHT +
      LegendShape.PADDING;

    // Create border/background rectangle
    const borderPoints = [
      new THREE.Vector3(position.x, position.y, 0),
      new THREE.Vector3(position.x + tableWidth, position.y, 0),
      new THREE.Vector3(position.x + tableWidth, position.y - tableHeight, 0),
      new THREE.Vector3(position.x, position.y - tableHeight, 0),
      new THREE.Vector3(position.x, position.y, 0),
    ];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const borderShape = new Autodesk.Edit2D.Polygon(borderPoints);
    shapes.push(borderShape);

    // Create color swatches for each entry (stored separately to preserve their colors)
    this.swatchShapes = [];
    this.hatchLineShapes = [];
    this.entries.forEach((entry, index) => {
      const rowY =
        position.y -
        LegendShape.PADDING -
        index * LegendShape.ROW_HEIGHT -
        LegendShape.ROW_HEIGHT / 2;

      // Color swatch position (centered vertically in row)
      const swatchX = position.x + LegendShape.PADDING;
      const swatchY = rowY + LegendShape.COLOR_SWATCH_SIZE / 2;
      const swatchSize = LegendShape.COLOR_SWATCH_SIZE;

      if (entry.isGradient && entry.gradientColors.length > 1) {
        // For gradients, create horizontal strips for each color
        const numColors = entry.gradientColors.length;
        const stripHeight = swatchSize / numColors;

        entry.gradientColors.forEach((color, colorIndex) => {
          const stripY = swatchY - colorIndex * stripHeight;
          const stripPoints = [
            new THREE.Vector3(swatchX, stripY, 0),
            new THREE.Vector3(swatchX + swatchSize, stripY, 0),
            new THREE.Vector3(swatchX + swatchSize, stripY - stripHeight, 0),
            new THREE.Vector3(swatchX, stripY - stripHeight, 0),
            new THREE.Vector3(swatchX, stripY, 0),
          ];

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const strip = new Autodesk.Edit2D.Polygon(stripPoints);
          (strip as any)._legendColor = color;
          (strip as any)._isGradientStrip = true;
          (strip as any)._entryId = entry.id;
          this.swatchShapes.push(strip);
          shapes.push(strip);
        });
      } else {
        // Solid color swatch
        const swatchPoints = [
          new THREE.Vector3(swatchX, swatchY, 0),
          new THREE.Vector3(swatchX + swatchSize, swatchY, 0),
          new THREE.Vector3(swatchX + swatchSize, swatchY - swatchSize, 0),
          new THREE.Vector3(swatchX, swatchY - swatchSize, 0),
          new THREE.Vector3(swatchX, swatchY, 0),
        ];

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const swatch = new Autodesk.Edit2D.Polygon(swatchPoints);
        (swatch as any)._legendColor = entry.baseColor || ChakraColor.gray400;
        (swatch as any)._entryId = entry.id;
        this.swatchShapes.push(swatch);
        shapes.push(swatch);
      }

      // Add hatch pattern lines if present
      if (
        entry.hatchPattern &&
        entry.hatchPattern.type !== HatchPatternType.None
      ) {
        const hatchShapes = this.createSwatchHatchLines(
          swatchX,
          swatchY,
          swatchSize,
          entry.hatchPattern,
          entry.baseColor
        );
        hatchShapes.forEach((shape) => {
          (shape as any)._entryId = entry.id;
          this.hatchLineShapes.push(shape);
          shapes.push(shape);
        });
      }
    });

    this.shapes = shapes;

    // Apply base style to main border shape
    this.setShapeStyle(borderShape, this.getDefaultStyleSet());

    // Apply swatch colors (must be done after setShapeStyle to avoid being overwritten)
    this.applySwatchColors();
  }

  /**
   * Check if an entry should be dimmed based on selection state
   */
  private isEntryDimmed(entryId: string): boolean {
    // Dim if no selection (all dimmed) or if this entry is not selected
    return !this.selectedEntryIds.has(entryId);
  }

  /**
   * Apply colors to swatch shapes (called after setStyle to preserve colors)
   */
  private applySwatchColors(): void {
    this.applySwatchShapeColors();
    this.applyHatchLineColors();
  }

  private applySwatchShapeColors(): void {
    for (const swatch of this.swatchShapes) {
      const color = (swatch as any)._legendColor || ChakraColor.gray400;
      const entryId = (swatch as any)._entryId;
      const isDimmed = entryId && this.isEntryDimmed(entryId);
      const alpha = isDimmed ? 0.3 : 1;

      swatch.style.fillColor = color;
      swatch.style.fillAlpha = alpha;

      // No border for gradient strips, thin border for solid swatches
      if ((swatch as any)._isGradientStrip) {
        swatch.style.lineColor = 'transparent';
        swatch.style.lineWidth = 0;
        swatch.style.lineAlpha = 0;
      } else {
        swatch.style.lineColor = ChakraColor.gray400;
        swatch.style.lineWidth = 0.5;
        swatch.style.lineAlpha = alpha;
      }
    }
  }

  private applyHatchLineColors(): void {
    for (const hatchLine of this.hatchLineShapes) {
      const lineColor =
        (hatchLine as any)._hatchLineColor || ChakraColor.gray700;
      const entryId = (hatchLine as any)._entryId;
      const isDimmed = entryId && this.isEntryDimmed(entryId);

      hatchLine.style.lineColor = lineColor;
      hatchLine.style.lineAlpha = isDimmed ? 0.3 : 1;
      hatchLine.style.lineWidth = 0.005;
      hatchLine.style.fillColor = 'transparent';
      hatchLine.style.fillAlpha = 0;
    }
  }

  /**
   * Create hatch lines for a swatch
   */
  private createSwatchHatchLines(
    x: number,
    y: number,
    size: number,
    pattern: HatchPatternConfig,
    baseColor: string
  ): Autodesk.Edit2D.Shape[] {
    const shapes: Autodesk.Edit2D.Shape[] = [];
    const angleRad = (pattern.angle * Math.PI) / 180;
    // Use a fixed gap for swatches (small relative to swatch size)
    const gap = size / 4;
    const lineWidth = 0.001;

    // Darken the base color for hatch lines
    const lineColor = this.darkenColor(baseColor, 0.7);

    // Generate diagonal lines across the swatch
    const generateLines = (angle: number) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const diagonal = size * Math.sqrt(2);

      // Generate lines perpendicular to the angle
      for (let offset = -diagonal; offset <= diagonal * 2; offset += gap) {
        // Calculate line endpoints
        const centerX = x + size / 2;
        const centerY = y - size / 2;

        // Line parallel to angle, offset perpendicular
        const perpX = -sin * offset;
        const perpY = cos * offset;

        const startX = centerX + perpX - cos * diagonal;
        const startY = centerY + perpY - sin * diagonal;
        const endX = centerX + perpX + cos * diagonal;
        const endY = centerY + perpY + sin * diagonal;

        // Clip to swatch bounds
        const clipped = this.clipLineToRect(
          startX,
          startY,
          endX,
          endY,
          x,
          y - size,
          x + size,
          y
        );

        if (clipped) {
          const pathData = `M ${clipped.x1.toFixed(4)},${clipped.y1.toFixed(
            4
          )} L ${clipped.x2.toFixed(4)},${clipped.y2.toFixed(4)}`;
          const svg = `<path d="${pathData}"/>`;

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const shape = Autodesk.Edit2D.Shape.fromSVG(svg);
          if (shape) {
            // Store line color for later reapplication after setStyle
            (shape as any)._hatchLineColor = lineColor;
            shape.style = {
              lineColor: lineColor,
              lineAlpha: 1,
              lineWidth: lineWidth,
              fillColor: 'transparent',
              fillAlpha: 0,
            };
            shapes.push(shape);
          }
        }
      }
    };

    // Primary lines
    generateLines(angleRad);

    // Secondary lines for crosshatch
    if (pattern.type === HatchPatternType.CrossHatch) {
      const secondAngle = pattern.secondAngle ?? pattern.angle + 90;
      generateLines((secondAngle * Math.PI) / 180);
    }

    return shapes;
  }

  /**
   * Clip a line to a rectangle, returning the clipped endpoints or null if outside
   */
  private clipLineToRect(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    minX: number,
    minY: number,
    maxX: number,
    maxY: number
  ): { x1: number; y1: number; x2: number; y2: number } | null {
    // Cohen-Sutherland line clipping algorithm (simplified)
    const INSIDE = 0,
      LEFT = 1,
      RIGHT = 2,
      BOTTOM = 4,
      TOP = 8;

    const computeCode = (x: number, y: number): number => {
      let code = INSIDE;
      if (x < minX) code |= LEFT;
      else if (x > maxX) code |= RIGHT;
      if (y < minY) code |= BOTTOM;
      else if (y > maxY) code |= TOP;
      return code;
    };

    let code1 = computeCode(x1, y1);
    let code2 = computeCode(x2, y2);

    // Maximum iterations to prevent infinite loops
    for (let iterations = 0; iterations < 10; iterations++) {
      if (!(code1 | code2)) {
        return { x1, y1, x2, y2 };
      } else if (code1 & code2) {
        return null;
      } else {
        const codeOut = code1 ? code1 : code2;
        let x = 0,
          y = 0;

        if (codeOut & TOP) {
          x = x1 + ((x2 - x1) * (maxY - y1)) / (y2 - y1);
          y = maxY;
        } else if (codeOut & BOTTOM) {
          x = x1 + ((x2 - x1) * (minY - y1)) / (y2 - y1);
          y = minY;
        } else if (codeOut & RIGHT) {
          y = y1 + ((y2 - y1) * (maxX - x1)) / (x2 - x1);
          x = maxX;
        } else if (codeOut & LEFT) {
          y = y1 + ((y2 - y1) * (minX - x1)) / (x2 - x1);
          x = minX;
        }

        if (codeOut === code1) {
          x1 = x;
          y1 = y;
          code1 = computeCode(x1, y1);
        } else {
          x2 = x;
          y2 = y;
          code2 = computeCode(x2, y2);
        }
      }
    }

    return null; // Failed to clip within max iterations
  }

  /**
   * Darken a hex color by a factor (0-1)
   */
  private darkenColor(hex: string, factor: number): string {
    // Handle non-hex colors
    if (!hex.startsWith('#')) return hex;

    const color = hex.replace('#', '');
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);

    const newR = Math.round(r * factor);
    const newG = Math.round(g * factor);
    const newB = Math.round(b * factor);

    return `#${newR.toString(16).padStart(2, '0')}${newG
      .toString(16)
      .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  /**
   * Override setStyle to preserve swatch colors after parent applies styles
   */
  protected setStyle(type: SheetShapeStyleType): void {
    super.setStyle(type);
    // Reapply swatch colors after parent style application
    this.applySwatchColors();
  }

  /**
   * Render PDF-space vector text labels for entry names
   * Uses individual text shapes to support per-entry coloring based on selection
   */
  private async renderTextLabels(): Promise<void> {
    if (!this.legendPosition || !this.textRenderer) return;

    const position = this.legendPosition;

    // Render each entry with appropriate color based on selection state
    for (let rowIndex = 0; rowIndex < this.entries.length; rowIndex++) {
      const entry = this.entries[rowIndex];
      const textX =
        position.x +
        LegendShape.PADDING +
        LegendShape.COLOR_SWATCH_SIZE +
        LegendShape.TEXT_PADDING;
      // Position text so baseline aligns with center of row
      const textY =
        position.y -
        LegendShape.PADDING -
        rowIndex * LegendShape.ROW_HEIGHT -
        LegendShape.ROW_HEIGHT / 2 -
        LegendShape.TEXT_HEIGHT * 0.3; // Adjust for baseline

      const isDimmed = this.isEntryDimmed(entry.id);
      const textColor = isDimmed ? ChakraColor.gray300 : ChakraColor.gray700;

      await this.textRenderer.addText(
        `${this.shapeId}-entry-${rowIndex}`,
        entry.name,
        { x: textX, y: textY },
        {
          fontSize: LegendShape.TEXT_HEIGHT,
          color: textColor,
        }
      );
    }
  }

  /**
   * Clear PDF-space text labels
   */
  private clearTextLabels(): void {
    if (this.textRenderer) {
      this.textRenderer.clear();
    }
  }

  public draw(): void {
    super.draw();

    // Initialize text renderer and render labels asynchronously
    if (!this.textRenderer) {
      this.textRenderer = new PdfSpaceTextRenderer(this.edit2d);
    }

    // Start rendering text (async, but we don't block on it)
    this.textRenderPromise = this.renderTextLabels().catch((err) => {
      console.warn('Failed to render legend text:', err);
    });
  }

  public remove(): void {
    // Clear text labels before removing shapes
    this.clearTextLabels();
    if (this.textRenderer) {
      this.textRenderer.dispose();
      this.textRenderer = null;
    }

    super.remove();
  }

  public hitTest(x: number, y: number): boolean {
    // Only check the main border shape for hit testing
    if (this.shapes.length > 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return this.shapes[0].hitTest(x, y, 0.1);
    }
    return false;
  }

  // Override getLabels to return empty - we render text as PDF-space vectors
  public getLabels(): {
    point: THREE.Vector3;
    label: string;
    rotation: undefined;
    fontSize: number | undefined;
  }[] {
    // Text is rendered as PDF-space vector shapes, not as screen-space HTML labels
    return [];
  }

  // Get the legend order ID for reference
  public getLegendOrderId(): string | null {
    return this.apiModel?.sheetShapeLegend?.legendOrderId || null;
  }

  // Get the order name
  public getOrderName(): string {
    return this.orderName;
  }
}
